import { mapActions, mapGetters, mapState } from 'vuex';

import * as S from './AccountModal.styled';
import { randomInt, headOfString } from '@util';

import Dialog from '@components/Common/Dialog';
import TrashIcon from '@svgIcons/trash.svg';

const COLORS = [
    '#B72B2B',
    '#E24141',
    '#FF8F8F',
    '#B90B57',
    '#EC3E8A',
    '#D862EA',
    '#A322B7',
    '#783F9F',
    '#957DF6',
    '#471198',
    '#1D4492',
    '#3266CD',
    '#42A3DB',
    '#295785',
    '#053E77',
    '#30B6BF',
    '#9CDBC0',
    '#50B68A',
    '#389955',
    '#2F7B14',
    '#9ECE3C',
    '#E1E652',
    '#E3B73C',
    '#DF8218',
];

const randomColor = () => {
    return COLORS[randomInt(0, COLORS.length - 1)];
};

const TYPE = {
    CREATE: 'CREATE',
    EDIT: 'EDIT',
    DELETE: 'DELETE',
};

export default {
    name: 'WorkspaceModal',
    props: {
        editMode: {
            type: Boolean,
            default: false,
        },
    },
    created() {
        if (this.editMode) {
            this.value = {
                ...this.account,
            };
        }
        this.updatedTeams = this.teams.length
            ? this.teams.map((team) => R.assoc('isDirty', false, team))
            : [];
    },
    data() {
        return {
            value: {
                color: randomColor(),
            },
            dirty: false,
            isDeleteMode: false,
            accountAlias: headOfString(this.account?.name) || 'd',
            updatedTeams: [],
            modalType: this.editMode ? TYPE.EDIT : TYPE.CREATE,
        };
    },
    computed: {
        ...mapGetters('user', [
            'account',
            'accounts',
            'teams',
            'isDefault',
            'isAppsumo',
        ]),
        ...mapState('user', ['subscription']),
        isTeamListEmpty() {
            return R.isEmpty(this.teams);
        },
        accountId: R.pathOr('', ['account', 'id']),
        isDeletable() {
            const { deletable } = this.value;
            return deletable && !this.isDefault;
        },
        anyInvlidValue() {
            return R.any(this.getErrorText, this.updatedTeams);
        },
    },
    render() {
        switch (this.modalType) {
            case TYPE.EDIT:
                return this.renderEditModal();
            case TYPE.DELETE:
                return this.renderDeleteModal();
            case TYPE.CREATE:
            default:
                return this.renderCreateModal();
        }
    },
    methods: {
        ...mapActions('user', ['deleteWorkspace', 'updateWorkspace']),
        updateActive(value) {
            this.$emit('update:active', value);
        },
        handleClick(e) {
            const color = R.path(['target', 'dataset', 'color'], e);
            if (!color) {
                return;
            }
            this.updateValue('color', color);
        },
        handleCreate() {
            const name = this.value.name || '';
            if (!name.trim()) {
                this.dirty = true;
                return;
            }
            this.$emit('confirm', this.value);
            this.updateActive(false);
        },
        handleEdit() {
            const name = this.value.name || '';
            if (!name.trim()) {
                this.dirty = true;
                return;
            }

            const teams = this.updatedTeams.map((team) => ({
                id: team.id,
                name: team.name,
            }));

            this.updateWorkspace({ name, teams }).then(() =>
                this.updateActive(false)
            );
        },
        handleDelete() {
            const { id } = this.account;

            this.deleteWorkspace(id);
            this.redesignedToast({
                message: this.$t('SETTINGS.toast_deleted'),
            });
            this.updateActive(false);
        },
        updateValue: R.curry(function (key, value) {
            this.value = R.assoc(key, value, this.value);
        }),
        isDuplicated({ name, id }) {
            return R.any(
                R.both(
                    R.propEq('name', name),
                    R.compose(R.not, R.propEq('id', id))
                ),
                this.updatedTeams
            );
        },
        getErrorText(team) {
            return team.isDirty && this.isDuplicated(team)
                ? this.$t('SETTINGS.error_team_name_duplicate')
                : '';
        },
        updateTeams: R.curry(function (idx, value) {
            this.updatedTeams = R.update(idx, value, this.updatedTeams);
        }),
        renderTeam(team, idx) {
            const _update = this.updateTeams(idx).bind(this);
            return (
                <S.Row>
                    <S.TeamLabelWrapper>
                        <S.LabelText>
                            {this.$t('SETTINGS.subheadline_team')}
                        </S.LabelText>
                    </S.TeamLabelWrapper>
                    <S.StatusInput
                        class="status-input-wrapper"
                        counterVisible={false}
                        value={this.updatedTeams[idx].name}
                        errorText={this.getErrorText(team)}
                        onInput={(name = '') => {
                            _update({ ...team, isDirty: true, name });
                        }}
                    />
                </S.Row>
            );
        },
        renderDeleteModal() {
            const props = {
                attrs: {
                    active: true,
                    confirmText: this.$t('SETTINGS.button_delete'),
                },
                on: {
                    'update:active': this.updateActive,
                    confirm: () => this.handleDelete(),
                    cancel: () => (this.modalType = TYPE.EDIT),
                },
            };

            const { name, color } = this.account;

            return (
                <Dialog {...props}>
                    <S.Wrapper>
                        <S.Body>
                            <S.ModalConfirmText>
                                {this.$t('SETTINGS.confirm_delete_workspace')}
                            </S.ModalConfirmText>
                            <S.CurrentNameWrapper style={{ marginTop: '12px' }}>
                                <S.AccountIcon color={color}>
                                    {this.accountAlias}
                                </S.AccountIcon>
                                <S.CurrentNameText>{name}</S.CurrentNameText>
                            </S.CurrentNameWrapper>
                        </S.Body>
                    </S.Wrapper>
                </Dialog>
            );
        },
        renderEditModal() {
            const props = {
                attrs: {
                    active: true,
                    confirmText: this.$t('SETTINGS.account_modal_confirm_edit'),
                    buttonDisabled: this.anyInvlidValue,
                    backdrop: true,
                },
                on: {
                    'update:active': this.updateActive,
                    confirm: this.handleEdit,
                    cancel: () => this.updateActive(false),
                },
            };

            const { name: accountName } = this.account;
            const { name: valueName } = this.value;

            return (
                <Dialog {...props}>
                    <S.Wrapper>
                        <S.Body>
                            <S.ModalTitle>
                                {this.$t('SETTINGS.account_modal_header_edit')}
                            </S.ModalTitle>
                            <S.LabelWrapper>
                                <S.LabelText>
                                    {this.$t('SETTINGS.current_workspace')}
                                </S.LabelText>
                            </S.LabelWrapper>
                            <S.CurrentNameWrapper>
                                <S.CurrentNameText>
                                    {accountName}
                                </S.CurrentNameText>
                            </S.CurrentNameWrapper>
                            <S.LabelWrapper>
                                <S.LabelText>
                                    {this.$t('SETTINGS.new_workspace')}
                                </S.LabelText>
                            </S.LabelWrapper>
                            <S.StatusInput
                                counterVisible={false}
                                value={valueName}
                                onInput={(value) =>
                                    this.updateValue('name', value)
                                }
                            />

                            <S.DeleteButtonCont
                                deletable={this.isDeletable}
                                onClick={() => {
                                    if (!this.isDeletable) {
                                        return;
                                    }
                                    if (
                                        this.isAppsumo &&
                                        this.subscription.status !== 'canceled'
                                    ) {
                                        return this.renderAppsumoCheckModal();
                                    }
                                    this.modalType = TYPE.DELETE;
                                }}
                            >
                                <TrashIcon v-view-box onClick={() => {}} />
                                {this.$t('SETTINGS.delete_workspace')}
                            </S.DeleteButtonCont>
                        </S.Body>
                        {!this.isTeamListEmpty && (
                            <S.Body>
                                <S.ModalTitle>
                                    {this.$t('SETTINGS.headline_edit_team')}
                                </S.ModalTitle>
                                <S.ScrollWrapper>
                                    {this.updatedTeams.map(this.renderTeam)}
                                </S.ScrollWrapper>
                            </S.Body>
                        )}
                    </S.Wrapper>
                </Dialog>
            );
        },
        renderCreateModal() {
            const { name = '' } = this.value;

            const props = {
                attrs: {
                    active: true,
                    confirmText: this.$t('SETTINGS.button_create'),
                    buttonDisabled: !name.trim(),
                    emitConfirmOnEnter: true,
                },
                on: {
                    'update:active': this.updateActive,
                    confirm: this.handleCreate,
                    cancel: () => this.updateActive(false),
                },
            };

            const inputProps = {
                attrs: {
                    value: name,
                    counterVisible: false,
                    error: this.dirty ? this.$t('COMMON.required') : '',
                    autoFocus: true,
                },
                on: {
                    input: (value = '') => {
                        if (value.trim()) {
                            this.dirty = false;
                        }
                        this.updateValue('name', value);
                    },
                },
            };

            return (
                <Dialog {...props}>
                    <S.Wrapper>
                        <S.Body>
                            <S.ModalTitle>
                                {this.$t('SETTINGS.headline_add_workspace')}
                            </S.ModalTitle>
                            <S.LabelWrapper>
                                <S.LabelText>
                                    {this.$t('SETTINGS.name_workspace')}
                                </S.LabelText>
                            </S.LabelWrapper>
                            <S.StatusInput {...inputProps} />
                            {/* Can be added someday */}
                            {/* <S.Colors nativeOnClick={this.handleClick}>
                                {COLORS.map((c) => (
                                    <S.ColorBox bgColor={c} data-color={c} />
                                ))}
                            </S.Colors> */}
                        </S.Body>
                    </S.Wrapper>
                </Dialog>
            );
        },
        renderAppsumoCheckModal() {
            return this.dialog({
                confirmText: this.$t('PAYMENT.appsumo_redirect_action_btn'),
                text: this.$t('PAYMENT.appsumo_redirect_action_desc'),
                confirmCb: () => {
                    const { invoice_item_uuid } = this.subscription;
                    return window.open(
                        `https://appsumo.8odi.net/c/3004200/416948/7443?sharedid=button1&u=https://appsumo.com/account/redemption/${invoice_item_uuid}/#change-plan`
                    );
                },
            });
        },
    },
};
